/**
 * The industries a report can be benchmarked against: the
 * `reportSectorBenchmarks`.`sector` enum minus `Other` and `Unknown`, which the
 * weekly aggregation excludes.
 *
 * Duplicated where this package isn't available at runtime, so keep all
 * copies in sync:
 * - `apps/api/src/architecture/domain/audit/BenchmarkSector.ts` (a test
 *   enforces it)
 * - `jobs/sectorBenchmarks/src/sectors.ts` (by hand; that job builds
 *   standalone via plain `tsc`)
 */
export const BENCHMARK_SECTORS = [
  'Retail',
  'Consumer Goods & FMCG',
  'Manufacturing & Industrial',
  'Automotive',
  'Pharmaceuticals & Life Sciences',
  'Healthcare',
  'Financial Services',
  'Insurance',
  'Technology & Software',
  'IT Services',
  'Telecommunications',
  'Media & Publishing',
  'Entertainment & Gaming',
  'Travel & Hospitality',
  'Transport & Logistics',
  'Mobility',
  'Energy & Utilities',
  'Construction & Real Estate',
  'Education',
  'Professional Services',
  'Digital Agencies & Web Development',
  'Marketing & Communications Agencies',
  'Public Sector / Government',
  'Non-Profit & Associations',
  'Sports & Fitness',
  'Wholesale & Distribution',
] as const;

export type BenchmarkSector = (typeof BENCHMARK_SECTORS)[number];

/**
 * Stored when the customer says none of the industries applies. It is in the
 * column's ENUM but carries no benchmark, so it reads back as "no industry"
 * while still blocking the AI guess.
 */
export const SECTOR_NONE = 'Other';

/** What the `sector` column may hold once a customer has touched it. */
export type StoredSector = BenchmarkSector | typeof SECTOR_NONE;

/**
 * Strips the spelling a sector arrives in. The same value reaches us as the
 * database literal ('Media & Publishing') and as Prisma's sanitised enum
 * member ('Media___Publishing'), so punctuation cannot be part of the key.
 */
function toKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const BY_KEY = new Map<string, BenchmarkSector>(
  BENCHMARK_SECTORS.map((sector) => [toKey(sector), sector]),
);

/** Canonical spelling of a benchmarkable sector, or null for anything else. */
export function normalizeSector(value: unknown): BenchmarkSector | null {
  if (typeof value !== 'string') return null;
  return BY_KEY.get(toKey(value)) ?? null;
}

interface SectorSource {
  /** The customer's own choice. Null until they make one. */
  sector?: string | null;
  aiCompanyData?: { sector?: unknown } | null;
}

/**
 * A stored `SECTOR_NONE` resolves to null rather than falling through, so
 * clearing the industry sticks. SQL readers get the same result from
 * `COALESCE(sector, aiCompanyData ->> '$.sector')`.
 */
export function resolveSector(
  entry: SectorSource | null | undefined,
): BenchmarkSector | null {
  if (!entry) return null;
  if (entry.sector) return normalizeSector(entry.sector);
  return normalizeSector(entry.aiCompanyData?.sector);
}
